/**
 * 一覧の見出しで並べ替え・絞り込み(2026-10-14 発注者指示: スプレッドシートのように見出しで操作したい)。
 *
 * - 見出しの項目名を押す → 並べ替えと絞り込みの窓が開く(2026-10-15 発注者指示で ▲▼・▾ の記号はやめた)
 *   - 並べ替え: 「小さい順・古い順」「大きい順・新しい順」
 *   - 絞り込み: Excel のオートフィルタと同じく、その列の値の一覧でチェックを付け外し
 *     (最初は全部チェック済み。値の検索・すべて選択・解除つき。選んだ瞬間に一覧へ反映)
 * - 並べ替え中・絞り込み中の列は、項目名を青色にする
 *
 * 絞り込みは「チェックを外した値を隠す」形で持つ。
 * 1つの画面に表が複数あり(支払い画面の③と⑤など)、絞り込みは画面全体に効くため、
 * 「選んだ値だけ表示」にすると、ほかの表にしか無い値まで消えてしまう(2026-10-15 確認)。
 *
 * 表の見出しは文字が長いと「…」で切るため(overflow:hidden)、絞り込みの窓は画面の最前面(body直下)に出す。
 * スマホは一覧がカード表示で見出しが無いため、従来どおり表の上の絞り込み欄で操作する(この部品はPCの表で使う)。
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

export type SheetSort = { key: string; dir: 'asc' | 'desc' } | null;
/** 列ごとの絞り込み(チェックを外した=隠す値)。列が無い・空配列 = 絞り込みなし */
export type SheetFilters = Record<string, string[]>;

/** 列の定義 */
export type SheetColumn<T> = {
  key: string;
  label: string;
  /** 絞り込みの選択肢に出す値(表示どおりの文字)。無ければ絞り込み不可 */
  text?: (row: T) => string;
  /** 並べ替えに使う値。無ければ text で並べる。text も無ければ並べ替え不可 */
  sortValue?: (row: T) => string | number;
};

/** 空欄の表示 */
export const BLANK = '(空欄)';

function norm(v: string): string {
  return v.trim() === '' || v === '—' ? BLANK : v;
}

/** 絞り込みと並べ替えを適用する */
export function applySheet<T>(rows: T[], columns: SheetColumn<T>[], filters: SheetFilters, sort: SheetSort): T[] {
  const active = columns.filter((c) => c.text && (filters[c.key]?.length ?? 0) > 0);
  const out = active.length
    ? rows.filter((r) => active.every((c) => !(filters[c.key] ?? []).includes(norm(c.text!(r)))))
    : [...rows];
  if (!sort) return out;
  const col = columns.find((c) => c.key === sort.key);
  const val = col?.sortValue ?? col?.text;
  if (!val) return out;
  const dir = sort.dir === 'desc' ? -1 : 1;
  return out.sort((a, b) => {
    const va = val(a);
    const vb = val(b);
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb), 'ja', { numeric: true }) * dir;
  });
}

/** 絞り込み中の列の数(「すべて解除」の表示などに使う) */
export function countSheetFilters(filters: SheetFilters): number {
  return Object.values(filters).filter((v) => v.length > 0).length;
}

type ThProps<T> = {
  column: SheetColumn<T>;
  /** 全列の定義(ほかの列の絞り込みを反映した行から選択肢を作るため。Excel と同じ挙動) */
  columns: SheetColumn<T>[];
  /** 絞り込み前の全件 */
  rows: T[];
  filters: SheetFilters;
  onFilters: (next: SheetFilters) => void;
  sort: SheetSort;
  onSort: (next: SheetSort) => void;
  style?: CSSProperties;
  title?: string;
};

export function SheetTh<T>({ column, columns, rows, filters, onFilters, sort, onSort, style, title }: ThProps<T>) {
  const thRef = useRef<HTMLTableCellElement | null>(null);
  const [open, setOpen] = useState(false);
  const sortable = !!(column.sortValue ?? column.text);
  const filterable = !!column.text;
  const hidden = filters[column.key] ?? [];
  const sortedHere = sort?.key === column.key ? sort.dir : null;

  // 2026-10-15 発注者指示: 見出しの ▲▼・▾ をやめ、項目名を押すと並べ替えと絞り込みの窓が開く形にする。
  // 並べ替え中・絞り込み中の列は、項目名の色で分かるようにする(記号は出さない)
  const active = !!sortedHere || hidden.length > 0;
  const state = [
    sortedHere ? (sortedHere === 'desc' ? '大きい順・新しい順で並べ替え中' : '小さい順・古い順で並べ替え中') : '',
    hidden.length ? `${hidden.length}件を非表示中` : '',
  ]
    .filter(Boolean)
    .join(' / ');

  return (
    <th ref={thRef} style={style} title={title ?? (state ? `${column.label}(${state})` : column.label)} className="sheet-th">
      <span className="sheet-th-inner">
        <button
          type="button"
          className={`sheet-th-label${sortable || filterable ? '' : ' plain'}${active ? ' on' : ''}`}
          onClick={() => setOpen((o) => !o)}
          disabled={!sortable && !filterable}
          aria-label={`${column.label}で並べ替え・絞り込み${state ? `(${state})` : ''}`}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          {column.label}
        </button>
      </span>
      {open && (
        <FilterPopover
          anchor={thRef.current}
          column={column}
          rows={applySheet(rows, columns, { ...filters, [column.key]: [] }, null)}
          hidden={hidden}
          sortedHere={sortedHere}
          onSort={(dir) => {
            onSort({ key: column.key, dir });
            setOpen(false);
          }}
          onChange={(next) => onFilters({ ...filters, [column.key]: next })}
          onClose={() => setOpen(false)}
        />
      )}
    </th>
  );
}

function FilterPopover<T>({
  anchor,
  column,
  rows,
  hidden,
  sortedHere,
  onSort,
  onChange,
  onClose,
}: {
  anchor: HTMLElement | null;
  column: SheetColumn<T>;
  rows: T[];
  hidden: string[];
  sortedHere: 'asc' | 'desc' | null;
  onSort: (dir: 'asc' | 'desc') => void;
  onChange: (nextHidden: string[]) => void;
  onClose: () => void;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [q, setQ] = useState('');

  // 値の一覧(件数つき)。並びは並べ替えと同じ基準(数字・日付は自然な順)
  const values = useMemo(() => {
    const count = new Map<string, { n: number; s: string | number }>();
    for (const r of column.text ? rows : []) {
      const v = norm(column.text!(r));
      const s = (column.sortValue ?? column.text)!(r);
      const cur = count.get(v);
      count.set(v, { n: (cur?.n ?? 0) + 1, s: cur?.s ?? s });
    }
    return [...count.entries()]
      .sort((a, b) => {
        if (a[0] === BLANK) return 1;
        if (b[0] === BLANK) return -1;
        const [sa, sb] = [a[1].s, b[1].s];
        if (typeof sa === 'number' && typeof sb === 'number') return sa - sb;
        return String(sa).localeCompare(String(sb), 'ja', { numeric: true });
      })
      .map(([v, x]) => ({ value: v, n: x.n }));
  }, [rows, column]);
  const shown = q ? values.filter((v) => v.value.toLowerCase().includes(q.toLowerCase())) : values;

  // 位置: 見出しのすぐ下。画面の右端からはみ出さないように寄せる
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  useEffect(() => {
    const place = () => {
      const r = anchor?.getBoundingClientRect();
      if (!r) return;
      const w = 260;
      setPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor]);

  // 外側のクリック・Esc で閉じる
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (boxRef.current?.contains(t) || anchor?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  // チェック = 表示する。チェックを外した値を hidden に入れる
  const isChecked = (v: string) => !hidden.includes(v);
  const toggle = (v: string) => onChange(hidden.includes(v) ? hidden.filter((x) => x !== v) : [...hidden, v]);
  const allShownChecked = shown.length > 0 && shown.every((v) => isChecked(v.value));
  const toggleAll = () => {
    const vals = shown.map((v) => v.value);
    onChange(allShownChecked ? [...new Set([...hidden, ...vals])] : hidden.filter((x) => !vals.includes(x)));
  };
  const visibleCount = values.filter((v) => isChecked(v.value)).length;

  return createPortal(
    <div ref={boxRef} className="sheet-pop" style={{ top: pos.top, left: pos.left }} role="dialog" aria-label={`${column.label}の絞り込み`}>
      {(column.sortValue ?? column.text) && (
        <div className="sheet-pop-sort">
          <button type="button" className={sortedHere === 'asc' ? 'on' : ''} onClick={() => onSort('asc')}>
            {sortedHere === 'asc' ? '✓ ' : ''}小さい順・古い順に並べ替え
          </button>
          <button type="button" className={sortedHere === 'desc' ? 'on' : ''} onClick={() => onSort('desc')}>
            {sortedHere === 'desc' ? '✓ ' : ''}大きい順・新しい順に並べ替え
          </button>
        </div>
      )}
      {column.text && values.length > 8 && (
        <input className="sheet-pop-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="値を検索" autoFocus />
      )}
      {column.text && (
      <div className="sheet-pop-list">
        <label className="sheet-pop-item all">
          <input type="checkbox" checked={allShownChecked} onChange={toggleAll} />
          <span>(すべて選択)</span>
        </label>
        {shown.map((v) => (
          <label key={v.value} className="sheet-pop-item">
            <input type="checkbox" checked={isChecked(v.value)} onChange={() => toggle(v.value)} />
            <span className="sheet-pop-value">{v.value}</span>
            <span className="sheet-pop-count">{v.n}</span>
          </label>
        ))}
        {shown.length === 0 && <div className="hint" style={{ padding: '6px 10px' }}>該当する値がありません</div>}
      </div>
      )}
      <div className="sheet-pop-foot">
        <span className="hint">{hidden.length ? `${values.length}件中 ${visibleCount}件を表示` : 'すべて表示中'}</span>
        <button type="button" className="txn-link" disabled={!hidden.length} onClick={() => onChange([])}>
          絞り込みを解除
        </button>
      </div>
    </div>,
    document.body,
  );
}
